import { createContext, useContext, useState, ReactNode } from "react";
import { Fraction as staticFractions } from "./staticData";

export const DEMO_ADDRESS = "0xdead000000000000000000000000000000000001" as const;

export interface SessionPlayerRecord {
  counterValue: string;
  hasWritten: boolean;
  counter: number;
  fraction: string;
  secret: number;
  wonAt: number;
  name: string;
  currentSeedNumbers: number[];
  story: string[];
}

export interface StoryPartRecord {
  counterValue: string;
  parent: string;
  orgSeed: number;
  author: string;
  created: number;
  faction: string;
  id: number;
  posVotes: number;
  negVotes: number;
  data: string;
}

interface SessionPlayerContextValue {
  address: typeof DEMO_ADDRESS;
  player: SessionPlayerRecord | null;
  sessionStories: StoryPartRecord[];
  createPlayer: (name: string, fractionName: string) => void;
  updatePlayer: (updates: Partial<SessionPlayerRecord>) => void;
  addSessionStory: (story: StoryPartRecord) => void;
}

const SessionPlayerContext = createContext<SessionPlayerContextValue>({
  address: DEMO_ADDRESS,
  player: null,
  sessionStories: [],
  createPlayer: () => {},
  updatePlayer: () => {},
  addSessionStory: () => {},
});

export function SessionPlayerProvider({ children }: { children: ReactNode }) {
  const [player, setPlayer] = useState<SessionPlayerRecord | null>(null);
  const [sessionStories, setSessionStories] = useState<StoryPartRecord[]>([]);

  const createPlayer = (name: string, fractionName: string) => {
    const fractionRecord = staticFractions.find(f => f.fractionName === fractionName);
    setPlayer({
      counterValue: DEMO_ADDRESS,
      hasWritten: false,
      counter: 0,
      fraction: fractionRecord?.counterValue ?? "",
      secret: 0,
      wonAt: 0,
      name,
      currentSeedNumbers: [],
      story: [],
    });
  };

  const updatePlayer = (updates: Partial<SessionPlayerRecord>) => {
    setPlayer(prev => (prev ? { ...prev, ...updates } : null));
  };

  const addSessionStory = (story: StoryPartRecord) => {
    setSessionStories(prev => [story, ...prev]);
  };

  return (
    <SessionPlayerContext.Provider value={{ address: DEMO_ADDRESS, player, sessionStories, createPlayer, updatePlayer, addSessionStory }}>
      {children}
    </SessionPlayerContext.Provider>
  );
}

export function useSessionPlayer() {
  return useContext(SessionPlayerContext);
}
